import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import {
    EMPLOYEE_ROW_LIMITS,
    getEmployeeColumn,
    moveEmployeeColumn,
    nextEmployeeSort,
    resolveEmployeeSettings,
    setEmployeeColumnVisible,
    toEmployeeSettingsPatch,
    type EmployeeColumnDef,
    type EmployeeColumnKey,
    type EmployeePerformanceSettings,
    type ResolvedEmployeeSettings,
    type SortDir,
} from '../../lib/employeePerformance'

// ── Employee Performance widget: the column editor ──────────────────────
//
// Opened from the widget's edit-mode ⚙ popover, and portalled to <body> by the caller: the grid
// positions every widget with a CSS transform, which would trap this dialog's position: fixed inside
// the widget. Edits a local draft; only Apply hands anything back. Follows the modal rule — the dimmed
// backdrop does nothing, only the ✕, Cancel and Apply close it.

export interface EmployeePerformanceConfigModalProps {
    settings: EmployeePerformanceSettings | undefined
    onApply: (patch: EmployeePerformanceSettings) => void
    onClose: () => void
    /** NET/GROSS wording for the sales column; the editor is not filter-aware, so NET by default. */
    includeReturns?: boolean
}

type MoveDir = 'up' | 'down'

/** react-grid-layout starts a widget drag on these; events from a portal still bubble through React. */
const stopGrid = (e: { stopPropagation: () => void }) => e.stopPropagation()

/** What Tab can land on inside the dialog — the focus trap cycles through these. */
const TABBABLE = 'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])'

const directionWords = (def: EmployeeColumnDef, dir: SortDir) =>
    def.numeric ? (dir === 'desc' ? 'highest first' : 'lowest first') : (dir === 'asc' ? 'A to Z' : 'Z to A')

export default function EmployeePerformanceConfigModal({ settings, onApply, onClose, includeReturns = true }: EmployeePerformanceConfigModalProps) {
    const [draft, setDraft] = useState<ResolvedEmployeeSettings>(() => resolveEmployeeSettings(settings))
    const [dragKey, setDragKey] = useState<EmployeeColumnKey | null>(null)
    const [overKey, setOverKey] = useState<EmployeeColumnKey | null>(null)
    const [announcement, setAnnouncement] = useState('')
    const baseId = useId()
    const dialogRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    /** After an arrow-button move, keep focus on the row that moved (a button can go disabled at an end). */
    const refocus = useRef<{ key: EmployeeColumnKey; dir: MoveDir } | null>(null)

    const ctx = { includeReturns }
    const nameOf = (key: EmployeeColumnKey) => getEmployeeColumn(key).menuLabel(ctx)

    useEffect(() => { dialogRef.current?.focus() }, [])

    useLayoutEffect(() => {
        const want = refocus.current
        if (!want) return
        refocus.current = null
        const row = listRef.current?.querySelector(`[data-col="${want.key}"]`)
        const same = row?.querySelector<HTMLButtonElement>(`[data-move="${want.dir}"]`)
        const other = row?.querySelector<HTMLButtonElement>(`[data-move="${want.dir === 'up' ? 'down' : 'up'}"]`)
        ;(same && !same.disabled ? same : other)?.focus()
    })

    const total = draft.columns.length
    const visibleCount = draft.columns.filter(c => c.visible).length

    const move = (from: number, to: number, via?: MoveDir) => {
        if (from < 0 || to < 0 || to >= total || from === to) return
        const key = draft.columns[from].key
        setDraft(d => ({ ...d, columns: moveEmployeeColumn(d.columns, from, to) }))
        if (via) refocus.current = { key, dir: via }
        setAnnouncement(`${nameOf(key)} moved to position ${to + 1} of ${total}.`)
    }

    const setVisible = (key: EmployeeColumnKey, visible: boolean) =>
        setDraft(d => ({ ...d, columns: setEmployeeColumnVisible(d.columns, key, visible) }))

    const toggleSort = (key: EmployeeColumnKey) => {
        const next = nextEmployeeSort({ key: draft.sortKey, dir: draft.sortDir }, key)
        setDraft(d => ({ ...d, sortKey: next.key, sortDir: next.dir }))
        setAnnouncement(`Sorted by ${nameOf(next.key)}, ${directionWords(getEmployeeColumn(next.key), next.dir)}.`)
    }

    const clearDrag = () => { setDragKey(null); setOverKey(null) }

    /** Which edge of the hovered row the dragged column will land on (moveEmployeeColumn semantics). */
    const dropEdge = (index: number): 'before' | 'after' | null => {
        if (!dragKey || overKey !== draft.columns[index]?.key) return null
        const from = draft.columns.findIndex(c => c.key === dragKey)
        if (from < 0 || from === index) return null
        return from < index ? 'after' : 'before'
    }

    const limits: number[] = (EMPLOYEE_ROW_LIMITS as readonly number[]).includes(draft.rowLimit)
        ? [...EMPLOYEE_ROW_LIMITS]
        : [...EMPLOYEE_ROW_LIMITS, draft.rowLimit].sort((a, b) => a - b)

    const sortColumnHidden = !draft.columns.find(c => c.key === draft.sortKey)?.visible

    const apply = () => {
        onApply(toEmployeeSettingsPatch(draft))
        onClose()
    }

    const reset = () => {
        setDraft(resolveEmployeeSettings(undefined))
        setAnnouncement('Columns, sorting and options reset to the defaults.')
    }

    // aria-modal alone doesn't hold the keyboard. The dialog is the last thing in <body>, so Tab from
    // Apply would leave the page, and Shift+Tab from the ✕ would reach the widget behind the backdrop —
    // its popover switches included, which this draft would then silently undo on Apply. Escape is
    // still ignored (the modal rule).
    const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab') return
        // Walked in document order and tested one by one: jsdom returns a selector list's matches
        // grouped by selector (every button, then every input), which would make the select "last".
        const items = [...(dialogRef.current?.querySelectorAll<HTMLElement>('*') ?? [])].filter(el => el.matches(TABBABLE))
        if (!items.length) return
        const first = items[0]
        const last = items[items.length - 1]
        const at = document.activeElement
        if (e.shiftKey && (at === first || at === dialogRef.current)) {
            e.preventDefault()
            last.focus()
        } else if (!e.shiftKey && at === last) {
            e.preventDefault()
            first.focus()
        }
    }

    return (
        <div className="modal-backdrop" onMouseDown={stopGrid} onPointerDown={stopGrid} onTouchStart={stopGrid}>
            <div ref={dialogRef} className="modal emp-cfg" role="dialog" aria-modal="true" aria-labelledby={`${baseId}-title`} tabIndex={-1} onKeyDown={trapTab}>
                <div className="modal-header">
                    <h2 id={`${baseId}-title`}>Employee Performance columns</h2>
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body emp-cfg-body">
                    <div className="emp-cfg-section-head">
                        <span className="emp-cfg-section-title">Columns</span>
                        <span className="emp-cfg-section-meta">{visibleCount} of {total} shown</span>
                    </div>
                    <p className="emp-cfg-intro">
                        Tick the columns to show and drag them (or use the arrows) into order. The sort button ranks the
                        rows by that column — press it again to reverse.
                    </p>

                    <ul ref={listRef} className="emp-cfg-list" aria-label="Columns">
                        {draft.columns.map((c, i) => {
                            const def = getEmployeeColumn(c.key)
                            const name = def.menuLabel(ctx)
                            const locked = !!def.lockedVisible
                            const active = draft.sortKey === c.key
                            const edge = dropEdge(i)
                            const checkId = `${baseId}-show-${c.key}`
                            const sortLabel = `Sort by ${name}, ${directionWords(def, active ? draft.sortDir : def.defaultDir)}`
                            return (
                                <li
                                    key={c.key}
                                    data-col={c.key}
                                    className={[
                                        'emp-cfg-row',
                                        c.visible ? '' : 'is-hidden',
                                        dragKey === c.key ? 'dragging' : '',
                                        edge ? `drop-${edge}` : '',
                                    ].filter(Boolean).join(' ')}
                                    draggable
                                    onDragStart={e => {
                                        setDragKey(c.key)
                                        if (e.dataTransfer) {
                                            e.dataTransfer.effectAllowed = 'move'
                                            // Firefox refuses to begin a drag whose dataTransfer is empty; this
                                            // payload only satisfies it. The drag state that matters is React's.
                                            e.dataTransfer.setData('text/plain', c.key)
                                        }
                                    }}
                                    onDragEnd={clearDrag}
                                    onDragOver={e => {
                                        if (!dragKey) return   // not one of our rows — leave the drop refused
                                        e.preventDefault()
                                        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
                                        if (overKey !== c.key) setOverKey(c.key)
                                    }}
                                    onDragLeave={e => {
                                        // Moving onto a child of this row is not leaving it.
                                        const to = e.relatedTarget as Node | null
                                        if (to && e.currentTarget.contains(to)) return
                                        setOverKey(cur => (cur === c.key ? null : cur))
                                    }}
                                    onDrop={e => {
                                        e.preventDefault()
                                        if (dragKey) move(draft.columns.findIndex(x => x.key === dragKey), i)
                                        clearDrag()
                                    }}
                                >
                                    <span className="emp-cfg-grip" aria-hidden="true" title="Drag to reorder">
                                        <GripVertical size={14} />
                                    </span>
                                    <input
                                        id={checkId}
                                        type="checkbox"
                                        className="emp-cfg-check"
                                        aria-label={`Show ${name}`}
                                        checked={c.visible}
                                        disabled={locked}
                                        onChange={e => setVisible(c.key, e.target.checked)}
                                    />
                                    <label htmlFor={checkId} className="emp-cfg-text">
                                        <span className="emp-cfg-label">
                                            {name}
                                            {locked && <span className="emp-cfg-badge">Always shown</span>}
                                        </span>
                                        <span className="emp-cfg-hint" title={def.description}>{def.description}</span>
                                    </label>
                                    {def.sortable ? (
                                        <button
                                            type="button"
                                            className="emp-cfg-sort"
                                            aria-pressed={active}
                                            aria-label={sortLabel}
                                            title={active ? `Sorted ${directionWords(def, draft.sortDir)} — press to reverse` : `Sort by ${name}`}
                                            onClick={() => toggleSort(c.key)}
                                        >
                                            {!active
                                                ? <ArrowUpDown size={14} aria-hidden="true" />
                                                : draft.sortDir === 'desc'
                                                    ? <ArrowDown size={14} aria-hidden="true" />
                                                    : <ArrowUp size={14} aria-hidden="true" />}
                                        </button>
                                    ) : (
                                        <span className="emp-cfg-sort-none" aria-hidden="true" />
                                    )}
                                    <span className="emp-cfg-move">
                                        <button type="button" data-move="up" aria-label={`Move ${name} up`} title="Move up"
                                            disabled={i === 0} onClick={() => move(i, i - 1, 'up')}>
                                            <ChevronUp size={14} aria-hidden="true" />
                                        </button>
                                        <button type="button" data-move="down" aria-label={`Move ${name} down`} title="Move down"
                                            disabled={i === total - 1} onClick={() => move(i, i + 1, 'down')}>
                                            <ChevronDown size={14} aria-hidden="true" />
                                        </button>
                                    </span>
                                </li>
                            )
                        })}
                    </ul>

                    <div className="emp-cfg-section-head">
                        <span className="emp-cfg-section-title">Options</span>
                    </div>
                    <div className="emp-cfg-options">
                        <div className="emp-cfg-toggles">
                            <label className="emp-cfg-toggle">
                                <input type="checkbox" checked={draft.showTotals}
                                    onChange={e => setDraft(d => ({ ...d, showTotals: e.target.checked }))} />
                                Show totals row
                            </label>
                            <label className="emp-cfg-toggle">
                                <input type="checkbox" checked={draft.showBars}
                                    onChange={e => setDraft(d => ({ ...d, showBars: e.target.checked }))} />
                                Show sales bars
                            </label>
                        </div>
                        <div className="emp-cfg-rows">
                            <label className="form-label" htmlFor={`${baseId}-rows`}>Rows shown</label>
                            <select id={`${baseId}-rows`} className="form-select" value={draft.rowLimit}
                                onChange={e => setDraft(d => ({ ...d, rowLimit: Number(e.target.value) }))}>
                                {limits.map(n => <option key={n} value={n}>{n === 0 ? 'All employees' : `Top ${n}`}</option>)}
                            </select>
                        </div>
                    </div>
                    {sortColumnHidden && (
                        <p className="emp-cfg-note">Rows are ranked by {nameOf(draft.sortKey)}, which is hidden.</p>
                    )}
                    <div className="sr-only" aria-live="polite">{announcement}</div>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary emp-cfg-reset" onClick={reset}>Reset to defaults</button>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={apply}>Apply</button>
                </div>
            </div>
        </div>
    )
}
